import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import MoveUpOutlinedIcon from "@mui/icons-material/MoveUpOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Button, Link, Paper, Stack, styled, Typography } from "@mui/material";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { assetService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { Licences } from "../../../Core/Api/Types/licences";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import {
  AreAllObjectsShareable,
  GetChildRecursivelyObjectById,
  GetChildsRecursivelyByIds,
  IsPermissionGranted,
} from "../../../Core/Utils/asset-object-utils";
import { GetTreeObj, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import { useAppSelector } from "../../../Redux/redux-store";
import { routePaths } from "../../../Router/router";
import { IsContentDeletable, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import useAssetManipulate from "../../../Utils/Hooks/useAssetTreeManipulation";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import ModalCopyAsset, { ModalCopyAssetElementProps } from "../../Modals/ModalCopyAsset/modal-copy-asset";
import ModalCreateAssetGroup, {
  ModalCreateAssetGroupElementProps,
} from "../../Modals/ModalCreateAssetGroup/modal-create-asset-group";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import ModalMoveAsset, { ModalMoveAssetElementProps } from "../../Modals/ModalMoveAsset/modal-move-asset";
import ModalSelectGroupParent, {
  ModalSelectGroupParentElementProps,
} from "../../Modals/ModalSelectGroupParent/modal-select-group-parent";

export type TreeViewActionsProps = {
  selection: number[];
  graph: DbAssetObjectInfos;
};

const TreeViewActions: FC<TreeViewActionsProps> = (props) => {
  const { t } = useTranslation();
  const { selection, graph } = props;
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueSnackbar = useCustomSnackbar();
  const { addObjectsToBasket } = useBasketActions();
  const {
    // util
    getIntersectLayerOfSelection,
    // create empty group
    canCreateGroupInAsset,
    createGroup,
    // Delete group(s)
    canDeleteGroups,
    deleteGroup,
    deleteGroups,
    // group assets
    canCreateGroupOfSelection,
    groupAssets,
    // move assets
    canMoveSelection,
    moveSelectionToAsset,
    // copy assets
    canCopySelection,
    copySelectionToAsset,
  } = useAssetManipulate();

  const userHasConfidentialityOrders = useMemo(() => UserHasAcl(AclTypes.aclTitleConfidentialityOrders), []);

  const title = useAppSelector(GetTreeObj(TreeContext.AssetHierarchy));
  // Tree
  const [selectedObjects, setSelectedObjects] = useState<DbAssetObjectInfos[]>([]);
  const [groupsActionsPerms, setGroupsActionsPerms] = useState<{
    isGroupSelected: boolean;
    isGroupWithChildSelected: boolean;
    areMultipleGroupsSelected: boolean;
  }>({
    isGroupSelected: false,
    isGroupWithChildSelected: false,
    areMultipleGroupsSelected: false,
  });
  const [canShareSelection, setCanShareSelection] = useState<boolean>(false);
  const [canOrderSelection, setCanOrderSelection] = useState<boolean>(false);

  const computeGroupsActionsPerms = (selectedObjects: DbAssetObjectInfos[]) => {
    const areMultipleGroupsSelected =
      selectedObjects.reduce((count, item) => (item.layer === ObjectLayer.Group ? ++count : count), 0) > 1;
    const isGroupSelected = areMultipleGroupsSelected
      ? true
      : selectedObjects.some((o) => o.layer === ObjectLayer.Group);
    const isGroupWithChildSelected = isGroupSelected
      ? selectedObjects.some((o) => o.layer === ObjectLayer.Group && o.childObjects?.length)
      : false;
    setGroupsActionsPerms({
      isGroupSelected,
      isGroupWithChildSelected,
      areMultipleGroupsSelected,
    });
  };

  // #region Move - Copy
  const handleMoveAssets = useCallback(
    (selectedParentId: number, closeModal: () => void, disableLoader: () => void) => {
      const _parentId = Number(selectedParentId);
      moveSelectionToAsset(
        selectedObjects.flatMap((o) => o.id),
        _parentId,
        closeModal,
        disableLoader
      );
    },
    [selectedObjects]
  );

  const onClickMoveSelection = useCallback(() => {
    if (!openModal) return;
    openModal<ModalMoveAssetElementProps>(ModalMoveAsset, {
      selectableObjectLayers: getIntersectLayerOfSelection(selectedObjects),
      objectIdsToDisable: selectedObjects
        .flatMap((o) => o.id)
        .concat(selectedObjects.flatMap((o) => o.parentObjectId!)),
      onMoveAssetClick: handleMoveAssets,
      objectIdsToHilight: selectedObjects.flatMap((o) => o.parentObjectId!),
    });
  }, [openModal, selectedObjects]);

  // Copy assets
  const handleCopyAssets = useCallback(
    (
      selectedParentId: number,
      selectedProviderId: number,
      closeModal: () => void,
      disableLoader: () => void,
      selectedPlaceholderId?: number
    ) => {
      const _parentId = Number(selectedParentId);
      copySelectionToAsset(
        selectedObjects.flatMap((o) => o.id),
        selectedProviderId,
        _parentId,
        selectedPlaceholderId,
        closeModal,
        disableLoader
      );
    },
    [selectedObjects]
  );

  const onClickCopySelection = useCallback(() => {
    if (!openModal) return;
    openModal<ModalCopyAssetElementProps>(ModalCopyAsset, {
      selectableObjectLayers: getIntersectLayerOfSelection(selectedObjects),
      objectIdsToDisable: selectedObjects
        .flatMap((o) => o.id)
        .concat(selectedObjects.flatMap((o) => o.parentObjectId!)),
      onCopyAssetClick: handleCopyAssets,
      objectIdsToHilight: selectedObjects.flatMap((o) => o.parentObjectId!),
    });
  }, [openModal, selectedObjects]);
  // #endregion
  // #region Share
  const userCanCreateShare = useMemo(() => {
    return (
      UserHasAcl(AclTypes.aclShareCreate) && !selectedObjects.some((x) => !IsPermissionGranted(x, PermissionType.Share))
    );
  }, [selectedObjects]);

  const openNewShareModal = () => {
    if (!openModal) return;
    openModal(ModalCreateShare, { object: title, assetIds: selection.map(Number) });
  };
  // #endregion
  // #region Create empty group
  const handleCreateEmptyGroupConfirm = useCallback(
    (groupName: string, closeModal: () => void, disableLoader: () => void) => {
      createGroup(groupName, selectedObjects[0], closeModal, disableLoader);
    },
    [selectedObjects]
  );

  const handleCreateEmptyGroup = useCallback(() => {
    if (!openModal) return;
    openModal<ModalCreateAssetGroupElementProps>(ModalCreateAssetGroup, {
      hasSelection: selectedObjects.length > 1,
      onConfirm: handleCreateEmptyGroupConfirm,
    });
  }, [selectedObjects, openModal, handleCreateEmptyGroupConfirm]);
  // #endregion
  // #region Group assets
  const handleGroupAssetsClick = useCallback(() => {
    if (!openModal) return;
    openModal<ModalCreateAssetGroupElementProps>(ModalCreateAssetGroup, {
      hasSelection: selectedObjects.length > 1,
      onConfirm: openSelectParentForGroup,
    });
  }, [selectedObjects, openModal]);

  const openSelectParentForGroup = useCallback(
    (groupName: string, closeModal: () => void) => {
      closeModal();
      if (!openModal) return;
      openModal<ModalSelectGroupParentElementProps>(ModalSelectGroupParent, {
        selectableObjectLayers: getIntersectLayerOfSelection(selectedObjects),
        objectsIdsToDisable: selectedObjects.flatMap((o) => o.id),
        onCreateGroupHereClick: (selectedAssetId, _closeModal, disableLoader) =>
          handleGroupAssets(selectedAssetId, groupName, _closeModal, disableLoader),
      });
    },
    [selectedObjects, openModal]
  );

  const handleGroupAssets = useCallback(
    (selectedParentId: number, groupName: string, closeModal: () => void, disableLoader: () => void) => {
      const parentObject = GetChildRecursivelyObjectById(title!, selectedParentId);
      if (parentObject)
        groupAssets(
          parentObject,
          groupName,
          selectedObjects.flatMap((o) => o.id),
          closeModal,
          disableLoader
        );
      else enqueueSnackbar(t("alert:error.parentObjectNotFound", { id: selectedParentId }), "error");
    },
    [title, selectedObjects]
  );
  // #endregion
  // #region Delete group(s)
  const userCanDeleteGroups = useMemo(() => {
    const groupsToDelete = selectedObjects.filter((o) => o.layer === ObjectLayer.Group && !o.childObjects?.length);
    if (!groupsToDelete.length) return false;

    return canDeleteGroups(groupsToDelete);
  }, [selectedObjects]);

  const handleDeleteGroups = useCallback(() => {
    const groupsToDelete = selectedObjects.filter((o) => o.layer === ObjectLayer.Group && !o.childObjects?.length);

    if (!groupsToDelete.length) return;

    if (groupsToDelete.length === 1) {
      deleteGroup(groupsToDelete[0]);
    } else {
      deleteGroups(groupsToDelete);
    }
  }, [selectedObjects, deleteGroup]);

  const openDeleteGroupsConfirmModal = useCallback(() => {
    const areMultipleGroupsSelected =
      selectedObjects.reduce((count, item) => (item.layer === ObjectLayer.Group ? ++count : count), 0) > 1;
    if (!openModal) return;
    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      title: areMultipleGroupsSelected
        ? t("modal:title.allSelectedGroupWillBeDeleted")
        : t("modal:title.selectedGroupWillBeDeleted"),
      onConfirm: handleDeleteGroups,
    });
  }, [selectedObjects, openModal]);
  // #endregion
  // #region Delete content
  const handleDeleteContentConfirm = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:comment.confirmDeleteContent"),
      title: t("modal:title.confirmDeleteContent"),
      onConfirm: handleDoDeleteContent,
    });
  };

  const handleDoDeleteContent = () => {
    assetService.DeleteObjectById(selectedObjects[0].id).then((response) => {
      if (response.success) enqueueSnackbar(t("alert:success.contentDeleted"), "success");
    });
  };
  // #endregion Delete content
  // hooks
  useEffect(() => {
    if (title) {
      const newSelectedObjects = GetChildsRecursivelyByIds(title, selection);
      setSelectedObjects(newSelectedObjects);
      computeGroupsActionsPerms(newSelectedObjects);
      setCanShareSelection(AreAllObjectsShareable(newSelectedObjects));
      setCanOrderSelection(
        !newSelectedObjects.some((o) => o.layer === ObjectLayer.Content) &&
          (!newSelectedObjects.some((o) => o.confidentialityInfos !== null) || userHasConfidentialityOrders)
      );
    }
  }, [selection]);

  return (
    <StyledPaper>
      {selection.length > 0 ? (
        <>
          {selection.length === 1 ? (
            <>
              <Typography variant="body2" mb={0.5} color="text.tier">
                {t("component:body.assetSelected", { count: 1 })}
              </Typography>
              <Link
                underline="hover"
                color="text.primary"
                component={RouterLink}
                to={`${routePaths.asset.path}/${selection[0]}`}
              >
                #{selection[0]}
              </Link>
              <Typography color="text.tier" mb={1}>
                {selectedObjects[0]?.name}
              </Typography>
              <Stack direction="row" spacing={1}>
                <AssetTypeChip type={selectedObjects[0]?.type!} variant="outlined" />
                <Typography color="text.tier">
                  {selectedObjects[0]?.childObjects === null
                    ? ""
                    : t("component:body.numberOfChildren", { count: selectedObjects[0]?.childObjects?.length })}
                </Typography>
              </Stack>
            </>
          ) : (
            <Typography>{t("component:body.assetSelected_other", { count: selection.length })}</Typography>
          )}
          <Typography color="text.tier" variant="h6" mt={3}>
            {t("common:actions")}
          </Typography>
          <Stack spacing={1} mt={1}>
            <Button
              fullWidth
              size="small"
              startIcon={<MoveUpOutlinedIcon />}
              onClick={onClickMoveSelection}
              disabled={!canMoveSelection(selectedObjects)}
            >
              {t("button:moveSelectionTo")}
            </Button>
            <Button
              fullWidth
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={onClickCopySelection}
              disabled={!canCopySelection(selectedObjects)}
            >
              {t("button:copySelectionTo")}
            </Button>
            {selectedObjects.length > 0 &&
              (!canMoveSelection(selectedObjects) || !canCopySelection(selectedObjects)) && (
                <Typography color="text.tier" variant="body2" textAlign="center" display="block" mt={1}>
                  {t("component:body.selectCannotBeMovedOrCopied")}
                </Typography>
              )}
            {userCanCreateShare && (
              <>
                <Button
                  fullWidth
                  size="small"
                  startIcon={<ShareOutlinedIcon />}
                  onClick={() => openNewShareModal()}
                  disabled={!canShareSelection || !UserHasLicence(Licences.ShareScreening)}
                >
                  {t("common:share")}
                </Button>
                {selectedObjects.length > 0 && !canShareSelection && (
                  <Typography color="text.tier" variant="body2" textAlign="center" display="block" mt={1}>
                    {t("component:body.onlyAcceptedItemsCanBeShared")}
                  </Typography>
                )}
              </>
            )}
            <Button
              fullWidth
              size="small"
              startIcon={<ShoppingBagOutlinedIcon />}
              onClick={() => addObjectsToBasket(selectedObjects)}
              disabled={!canOrderSelection}
            >
              {t("button:addSelectionToBasket")}
            </Button>
            {selectedObjects.length > 0 && !canOrderSelection && (
              <Typography color="text.tier" variant="body2" textAlign="center" display="block" mt={1}>
                {t("component:body.contentAndConfidentialCannotBeOrdered")}
              </Typography>
            )}
            {selectedObjects.length === 1 && (
              <Button
                fullWidth
                size="small"
                startIcon={<CreateNewFolderOutlinedIcon />}
                disabled={!canCreateGroupInAsset(selectedObjects[0])}
                onClick={handleCreateEmptyGroup}
              >
                {t("button:createGroupHere")}
              </Button>
            )}
            {selectedObjects.length > 1 && (
              <Button
                fullWidth
                size="small"
                startIcon={<DriveFileMoveOutlinedIcon />}
                disabled={!canCreateGroupOfSelection(selectedObjects)}
                onClick={handleGroupAssetsClick}
              >
                {t("button:groupSelectionIn")}
              </Button>
            )}

            {groupsActionsPerms.isGroupSelected && userCanDeleteGroups && (
              <Button
                fullWidth
                size="small"
                color="error"
                startIcon={<DeleteOutlinedIcon />}
                variant="outlined"
                disabled={groupsActionsPerms.isGroupWithChildSelected}
                onClick={openDeleteGroupsConfirmModal}
              >
                {groupsActionsPerms.areMultipleGroupsSelected ? t("button:deleteGroups") : t("button:deleteGroup")}
              </Button>
            )}
            {groupsActionsPerms.isGroupWithChildSelected && (
              <Typography color="text.tier" variant="body2" textAlign="center" display="block" mt={1}>
                {t("component:body.cannotDeleteGroupWithChildren")}
              </Typography>
            )}

            {selectedObjects.length === 1 &&
              (selectedObjects[0].layer === ObjectLayer.Content || selectedObjects[0].layer === ObjectLayer.Group) &&
              IsContentDeletable(selectedObjects[0]) && (
                <Button
                  fullWidth
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlinedIcon />}
                  variant="outlined"
                  onClick={handleDeleteContentConfirm}
                >
                  {t("common:delete")} {selectedObjects[0] ? selectedObjects[0].layer : ""}
                </Button>
              )}
          </Stack>
        </>
      ) : (
        <>
          <Typography>{t("component:body.noAssetSelected")}</Typography>
          <Typography color="text.tier">{t("component:body.selectAssetToManageHierarchy")}</Typography>
        </>
      )}
    </StyledPaper>
  );
};

export default TreeViewActions;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: "100%",
  backgroundImage: "none",
}));
